import React, { Component } from 'react'
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Drawer,
    Empty,
    Form,
    Input,
    Modal,
    Progress,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd'
import {
    ApiOutlined,
    AppstoreAddOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    FileSearchOutlined,
    FolderAddOutlined,
    PlusOutlined,
    ReloadOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons'
import {
    addApiModuleMembers,
    addApiModuleMembersBySource,
    changeApiModuleStatus,
    getApiClassificationProgress,
    getApiModuleDetail,
    getApiModuleList,
    getApiModuleSuggestions,
    getVibeModuleUsage,
    getUnclassifiedApiList,
    removeApiModuleMembers,
    saveApiModule,
    setApiModulePrimary,
    updateApiModule,
} from '../../request'

const { Text, Paragraph } = Typography
const { TextArea } = Input

const STATUS_META = {
    draft: { label: '草稿', color: 'default' },
    active: { label: '在用', color: 'blue' },
    disabled: { label: '停用', color: 'red' },
}

const SENSITIVE_META = {
    low: { label: '低', color: 'green' },
    medium: { label: '中', color: 'orange' },
    high: { label: '高', color: 'red' },
}

const CONFIDENCE_META = {
    high: { label: '高', color: 'green' },
    medium: { label: '中', color: 'orange' },
    none: { label: '—', color: 'default' },
}

const METHOD_COLOR = { GET: 'green', POST: 'blue', PUT: 'orange', DELETE: 'red', PATCH: 'purple' }

export default class McpApiModule extends Component {
    formRef = React.createRef()

    state = {
        loading: false,
        modules: [],
        progress: null,
        moduleUsage: {},
        keyword: '',
        domainCode: undefined,
        status: undefined,

        detailVisible: false,
        detailLoading: false,
        detail: null,

        formVisible: false,
        formSubmitting: false,
        editingModule: null,

        memberVisible: false,
        memberLoading: false,
        memberSubmitting: false,
        memberPrimary: true,
        memberSelectedCodes: [],
        candidateLoading: false,
        candidates: [],
        candidateTotal: 0,
        candidatePage: 1,
        candidateKeyword: '',
        candidateSource: undefined,
        batchSourceSystem: '',

        suggestVisible: false,
        suggestLoading: false,
        suggestions: [],
    }

    componentDidMount() {
        this.loadModules()
    }

    loadModules = () => {
        this.setState({ loading: true })
        Promise.all([
            getApiModuleList({}).catch(() => []),
            getApiClassificationProgress({}).catch(() => null),
            getVibeModuleUsage({}).catch(() => []),
        ]).then(([modules, progress, usage]) => {
            const moduleUsage = {}
            ;(Array.isArray(usage) ? usage : []).forEach((row) => {
                if (row?.moduleCode) moduleUsage[row.moduleCode] = row.projectCount || 0
            })
            this.setState({
                loading: false,
                modules: Array.isArray(modules) ? modules : (modules?.list || []),
                progress: progress || null,
                moduleUsage,
            })
        })
    }

    openDetail = (moduleCode) => {
        this.setState({ detailVisible: true, detailLoading: true, detail: null })
        getApiModuleDetail({ moduleCode })
            .then((res) => {
                this.setState({ detail: res || null, detailLoading: false })
            })
            .catch(() => this.setState({ detailLoading: false }))
    }

    reloadDetail = () => {
        const detail = this.state.detail
        if (!detail?.module?.moduleCode) return
        this.openDetail(detail.module.moduleCode)
    }

    openCreate = () => {
        this.setState({ formVisible: true, editingModule: null })
        setTimeout(() => this.formRef.current?.resetFields(), 0)
    }

    openEdit = (record) => {
        this.setState({ formVisible: true, editingModule: record })
        setTimeout(() => {
            this.formRef.current?.setFieldsValue({
                moduleCode: record.moduleCode,
                moduleName: record.moduleName,
                domainCode: record.domainCode,
                domainName: record.domainName,
                permissionSource: record.permissionSource,
                owner: record.owner,
                sensitiveLevel: record.sensitiveLevel || 'low',
                status: record.status || 'draft',
                sampleQuestions: record.sampleQuestions,
                remark: record.remark,
            })
        }, 0)
    }

    submitForm = () => {
        const form = this.formRef.current
        form.validateFields().then((values) => {
            const editing = this.state.editingModule
            const payload = { ...values }
            if (!editing) {
                delete payload.moduleCode
            }
            this.setState({ formSubmitting: true })
            const request = editing
                ? updateApiModule({ ...payload, moduleCode: editing.moduleCode })
                : saveApiModule(payload)
            request
                .then(() => {
                    message.success(editing ? '模块已更新' : '模块已创建')
                    this.setState({ formVisible: false, formSubmitting: false, editingModule: null })
                    this.loadModules()
                    if (editing && this.state.detailVisible) this.reloadDetail()
                })
                .catch((error) => {
                    this.setState({ formSubmitting: false })
                    message.error(this.readError(error))
                })
        })
    }

    toggleStatus = (record) => {
        const target = record.status === 'active' ? 'disabled' : 'active'
        Modal.confirm({
            title: `确认把「${record.moduleName}」设为${STATUS_META[target].label}？`,
            content: target === 'disabled'
                ? '停用后，应用再保存该模块会被拒绝；已经挂在应用上的接口不会自动回收。'
                : '设为在用后，应用才能勾选这个模块。',
            okText: '确认',
            cancelText: '取消',
            onOk: () => changeApiModuleStatus({ moduleCode: record.moduleCode, status: target })
                .then(() => {
                    message.success('状态已更新')
                    this.loadModules()
                })
                .catch((error) => message.error(this.readError(error))),
        })
    }

    // ---------------- 添加接口 ----------------
    openMembers = () => {
        this.setState({
            memberVisible: true,
            memberSelectedCodes: [],
            memberPrimary: true,
            candidatePage: 1,
            candidateKeyword: '',
            candidateSource: undefined,
        })
        this.loadCandidates()
    }

    loadCandidates = () => {
        const { candidatePage, candidateKeyword, candidateSource } = this.state
        this.setState({ candidateLoading: true })
        getUnclassifiedApiList({
            keyword: candidateKeyword || undefined,
            sourceSystem: candidateSource,
            pageNum: candidatePage,
            pageSize: 20,
        })
            .then((res) => {
                this.setState({
                    candidates: res?.list || [],
                    candidateTotal: res?.total || 0,
                    candidateLoading: false,
                })
            })
            .catch(() => this.setState({ candidateLoading: false }))
    }

    submitMembers = () => {
        const detail = this.state.detail
        const moduleCode = detail?.module?.moduleCode
        const apiCodes = this.state.memberSelectedCodes
        if (!moduleCode || !apiCodes.length) {
            message.warning('请先勾选要加入的接口')
            return
        }
        this.setState({ memberSubmitting: true })
        addApiModuleMembers({ moduleCode, apiCodes, primary: this.state.memberPrimary })
            .then((res) => {
                this.setState({ memberSubmitting: false, memberVisible: false })
                const parts = [`新增 ${res?.addedCount || 0} 条`]
                if (res?.referenceCount) parts.push(`其中引用 ${res.referenceCount} 条`)
                if (res?.downgradedCount) parts.push(`${res.downgradedCount} 条已有主模块，按引用加入`)
                if (res?.skippedCount) parts.push(`跳过 ${res.skippedCount} 条`)
                message.success(parts.join('，'))
                this.loadModules()
                this.reloadDetail()
            })
            .catch((error) => {
                this.setState({ memberSubmitting: false })
                message.error(this.readError(error))
            })
    }

    submitMembersBySource = () => {
        const moduleCode = this.state.detail?.module?.moduleCode
        const sourceSystem = (this.state.batchSourceSystem || '').trim()
        if (!moduleCode) return
        if (!sourceSystem) {
            message.warning('请先填写来源系统，例如 lechun_oms_v2')
            return
        }
        this.setState({ memberSubmitting: true })
        getUnclassifiedApiList({ sourceSystem, pageNum: 1, pageSize: 1 })
            .then((res) => {
                const total = res?.total || 0
                this.setState({ memberSubmitting: false })
                if (!total) {
                    message.info(`${sourceSystem} 下没有未归类的接口`)
                    return
                }
                Modal.confirm({
                    title: `把 ${sourceSystem} 下 ${total} 条未归类接口整批加入？`,
                    content: total > 200
                        ? '单次最多处理 200 条，剩余部分可以再执行一次。'
                        : '新增接口默认不公开，需要单独设为公开后应用才能授权。',
                    okText: '确认加入',
                    cancelText: '取消',
                    onOk: () => addApiModuleMembersBySource({
                        moduleCode,
                        sourceSystem,
                        primary: this.state.memberPrimary,
                        limit: 200,
                    }).then((result) => {
                        message.success(`整批加入完成，新增 ${result?.addedCount || 0} 条，跳过 ${result?.skippedCount || 0} 条`)
                        this.setState({ memberVisible: false })
                        this.loadModules()
                        this.reloadDetail()
                    }).catch((error) => message.error(this.readError(error))),
                })
            })
            .catch(() => this.setState({ memberSubmitting: false }))
    }

    removeMember = (member) => {
        const moduleCode = this.state.detail?.module?.moduleCode
        if (!moduleCode) return
        Modal.confirm({
            title: `确认把「${member.apiName || member.apiCode}」移出该模块？`,
            okText: '移除',
            cancelText: '取消',
            onOk: () => removeApiModuleMembers({ moduleCode, apiCodes: [member.apiCode] })
                .then(() => {
                    message.success('已移出模块')
                    this.loadModules()
                    this.reloadDetail()
                })
                .catch((error) => message.error(this.readError(error))),
        })
    }

    setPrimary = (member) => {
        const moduleCode = this.state.detail?.module?.moduleCode
        if (!moduleCode) return
        setApiModulePrimary({ moduleCode, apiCode: member.apiCode })
            .then(() => {
                message.success('已设为主模块')
                this.reloadDetail()
            })
            .catch((error) => message.error(this.readError(error)))
    }

    // ---------------- 批量归类 ----------------
    openSuggest = () => {
        this.setState({ suggestVisible: true, suggestLoading: true, suggestions: [] })
        getApiModuleSuggestions({ limit: 200 })
            .then((res) => this.setState({ suggestions: Array.isArray(res) ? res : [], suggestLoading: false }))
            .catch(() => this.setState({ suggestLoading: false }))
    }

    adoptSuggestion = (row) => {
        if (!row.suggestedModuleCode) return
        addApiModuleMembers({ moduleCode: row.suggestedModuleCode, apiCodes: [row.apiCode], primary: true })
            .then(() => {
                message.success(`已归入 ${row.suggestedModuleName || row.suggestedModuleCode}`)
                this.setState({ suggestions: this.state.suggestions.filter((item) => item.apiCode !== row.apiCode) })
                this.loadModules()
            })
            .catch((error) => message.error(this.readError(error)))
    }

    adoptAllHighConfidence = () => {
        const grouped = {}
        this.state.suggestions
            .filter((row) => row.confidence === 'high' && row.suggestedModuleCode)
            .forEach((row) => {
                if (!grouped[row.suggestedModuleCode]) grouped[row.suggestedModuleCode] = []
                grouped[row.suggestedModuleCode].push(row.apiCode)
            })
        const moduleCodes = Object.keys(grouped)
        if (!moduleCodes.length) {
            message.info('没有高置信度的建议可采纳')
            return
        }
        Modal.confirm({
            title: `按建议归入 ${moduleCodes.length} 个模块？`,
            content: `将影响 ${Object.values(grouped).reduce((sum, list) => sum + list.length, 0)} 条接口。`,
            okText: '确认归类',
            cancelText: '取消',
            onOk: () => Promise.all(moduleCodes.map((moduleCode) => addApiModuleMembers({
                moduleCode,
                apiCodes: grouped[moduleCode],
                primary: true,
            })))
                .then(() => {
                    message.success('批量归类完成')
                    this.setState({ suggestVisible: false })
                    this.loadModules()
                })
                .catch((error) => message.error(this.readError(error))),
        })
    }

    readError = (error) => {
        if (!error) return '操作失败'
        if (typeof error === 'string') return error
        return error.message || error.msg || '操作失败'
    }

    formatDate = (value) => {
        if (!value) return '-'
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return String(value)
        const pad = (n) => String(n).padStart(2, '0')
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    renderHero = () => {
        const { modules, progress } = this.state
        const total = progress?.totalCount || 0
        const classified = progress?.classifiedCount || 0
        const percent = total ? Math.round((classified / total) * 100) : 0
        const cards = [
            { title: '能力模块', value: modules.length, color: '#1951d1', icon: <AppstoreAddOutlined /> },
            { title: '已归类接口', value: classified, color: '#2f9e44', icon: <ApiOutlined /> },
            { title: '未归类接口', value: progress?.unclassifiedCount || 0, color: '#d9480f', icon: <ThunderboltOutlined /> },
            { title: '归类进度', value: `${percent}%`, color: '#0f9c95', icon: <FileSearchOutlined /> },
        ]
        return (
            <div style={{ marginBottom: 16 }}>
                <div style={{
                    padding: '24px 26px',
                    borderRadius: 20,
                    border: '1px solid #dce6ff',
                    background: 'linear-gradient(135deg, #f7fbff 0%, #edf4ff 52%, #f2fffc 100%)',
                    marginBottom: 16,
                }}>
                    <div style={{ color: '#1951d1', fontWeight: 700, fontSize: 13, letterSpacing: 0.6, marginBottom: 8 }}>
                        Capability Modules
                    </div>
                    <div style={{ fontSize: 28, lineHeight: 1.3, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
                        能力模块
                    </div>
                    <Text type="secondary" style={{ fontSize: 14, lineHeight: 1.8 }}>
                        用业务语言定义能力，填写数据范围来源，再把接口归入模块。模块是应用授权的单位。
                    </Text>
                </div>
                <Row gutter={[12, 12]}>
                    {cards.map((card) => (
                        <Col xs={24} sm={12} xl={6} key={card.title}>
                            <Card
                                bordered={false}
                                style={{
                                    borderRadius: 18,
                                    border: `1px solid ${card.color}1f`,
                                    background: `linear-gradient(180deg, ${card.color}12 0%, #ffffff 68%)`,
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <div style={{
                                        width: 46,
                                        height: 46,
                                        borderRadius: 14,
                                        background: `${card.color}14`,
                                        color: card.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 20,
                                    }}>
                                        {card.icon}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{card.title}</div>
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>{card.value}</div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        )
    }

    renderProgressBar = () => {
        const { progress } = this.state
        const pending = progress?.pendingBySource || []
        if (!pending.length) return null
        return (
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16, borderRadius: 12 }}
                message="未归类接口按来源系统分布，可在接口管理页按来源系统整批归入模块"
                description={(
                    <Space wrap>
                        {pending.slice(0, 8).map((item) => (
                            <Tag key={item.sourceSystem}>{item.sourceSystem} {item.apiCount}</Tag>
                        ))}
                    </Space>
                )}
            />
        )
    }

    renderToolbar = () => {
        const { keyword, domainCode, status, modules } = this.state
        const domains = Array.from(new Set(modules.map((item) => item.domainCode).filter(Boolean)))
        return (
            <div style={{
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
                padding: 14,
                background: '#f8fafc',
                borderRadius: 16,
                border: '1px solid #eef2f7',
                marginBottom: 16,
                alignItems: 'center',
            }}>
                <Input
                    allowClear
                    value={keyword}
                    placeholder="搜索模块名称、编码、负责人"
                    style={{ width: 260 }}
                    onChange={(e) => this.setState({ keyword: e.target.value })}
                />
                <Select
                    allowClear
                    value={domainCode}
                    placeholder="业务域"
                    style={{ width: 160 }}
                    options={domains.map((item) => ({ label: item, value: item }))}
                    onChange={(value) => this.setState({ domainCode: value })}
                />
                <Select
                    allowClear
                    value={status}
                    placeholder="状态"
                    style={{ width: 140 }}
                    options={Object.keys(STATUS_META).map((key) => ({ label: STATUS_META[key].label, value: key }))}
                    onChange={(value) => this.setState({ status: value })}
                />
                <Button icon={<ReloadOutlined />} onClick={this.loadModules}>刷新</Button>
                <Space style={{ marginLeft: 'auto' }}>
                    <Button icon={<ThunderboltOutlined />} onClick={this.openSuggest}>批量归类</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={this.openCreate}>新建模块</Button>
                </Space>
            </div>
        )
    }

    renderColumns = () => [
        {
            title: '模块',
            key: 'module',
            width: 260,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600, color: '#111827' }}>{record.moduleName}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.moduleCode}</Text>
                    {record.domainName ? <Tag style={{ marginLeft: 8 }}>{record.domainName}</Tag> : null}
                </div>
            ),
        },
        {
            title: '接口',
            key: 'apiCount',
            width: 110,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{record.apiCount || 0}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>公开 {record.publicApiCount || 0}</Text>
                </div>
            ),
        },
        {
            title: '工具',
            key: 'toolCount',
            width: 80,
            render: (_, record) => record.toolCount || 0,
        },
        {
            title: '权限来源',
            dataIndex: 'permissionSource',
            key: 'permissionSource',
            width: 240,
            render: (value) => value
                ? <Text style={{ fontSize: 12 }}>{value}</Text>
                : <Text type="warning" style={{ fontSize: 12 }}>待指定</Text>,
        },
        {
            title: '负责人',
            dataIndex: 'owner',
            key: 'owner',
            width: 100,
            render: (value) => value || <Text type="secondary">-</Text>,
        },
        {
            title: '敏感',
            dataIndex: 'sensitiveLevel',
            key: 'sensitiveLevel',
            width: 80,
            render: (value) => {
                const meta = SENSITIVE_META[value] || SENSITIVE_META.low
                return <Tag color={meta.color}>{meta.label}</Tag>
            },
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 90,
            render: (value) => {
                const meta = STATUS_META[value] || STATUS_META.draft
                return <Tag color={meta.color}>{meta.label}</Tag>
            },
        },
        {
            title: '引用应用',
            key: 'appCount',
            width: 100,
            render: (_, record) => this.state.moduleUsage[record.moduleCode] || 0,
        },
        {
            title: '更新',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 150,
            render: (value) => <Text type="secondary" style={{ fontSize: 12 }}>{this.formatDate(value)}</Text>,
        },
        {
            title: '操作',
            key: 'action',
            width: 220,
            fixed: 'right',
            render: (_, record) => (
                <Space wrap size={4}>
                    <Button size="small" onClick={() => this.openDetail(record.moduleCode)}>详情</Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => this.openEdit(record)}>编辑</Button>
                    <Tooltip title={record.status === 'active' ? '停用后应用不能再勾选该模块' : '设为在用后应用可以勾选'}>
                        <Button size="small" onClick={() => this.toggleStatus(record)}>
                            {record.status === 'active' ? '停用' : '启用'}
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ]

    renderDetailDrawer = () => {
        const { detailVisible, detailLoading, detail } = this.state
        const module = detail?.module
        const members = detail?.members || []
        return (
            <Drawer
                width={880}
                title={module ? `模块详情 · ${module.moduleName}` : '模块详情'}
                visible={detailVisible}
                onClose={() => this.setState({ detailVisible: false })}
                extra={module ? (
                    <Space>
                        <Button icon={<FolderAddOutlined />} onClick={this.openMembers}>添加接口</Button>
                        <Button type="primary" icon={<EditOutlined />} onClick={() => this.openEdit(module)}>编辑</Button>
                    </Space>
                ) : null}
            >
                {detailLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin /></div>
                ) : !module ? (
                    <Empty description="暂无模块数据" />
                ) : (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="模块编码">{module.moduleCode}</Descriptions.Item>
                            <Descriptions.Item label="业务域">{module.domainName}（{module.domainCode}）</Descriptions.Item>
                            <Descriptions.Item label="状态">
                                <Tag color={(STATUS_META[module.status] || STATUS_META.draft).color}>
                                    {(STATUS_META[module.status] || STATUS_META.draft).label}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="负责人">{module.owner || '-'}</Descriptions.Item>
                            <Descriptions.Item label="敏感级别">
                                <Tag color={(SENSITIVE_META[module.sensitiveLevel] || SENSITIVE_META.low).color}>
                                    {(SENSITIVE_META[module.sensitiveLevel] || SENSITIVE_META.low).label}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="接口 / 公开 / 工具">
                                {module.apiCount || 0} / {module.publicApiCount || 0} / {module.toolCount || 0}
                            </Descriptions.Item>
                            <Descriptions.Item label="权限来源" span={2}>
                                {module.permissionSource || <Text type="warning">未填写，模块只能停留在草稿</Text>}
                            </Descriptions.Item>
                            <Descriptions.Item label="说明" span={2}>{module.remark || '-'}</Descriptions.Item>
                        </Descriptions>

                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>示例问句（会进入使用者提示词）</div>
                            {(detail.questions || []).length ? (
                                <Space direction="vertical">
                                    {detail.questions.map((question) => (
                                        <Tag key={question} style={{ fontSize: 13, padding: '4px 10px' }}>{question}</Tag>
                                    ))}
                                </Space>
                            ) : (
                                <Text type="secondary">还没有示例问句</Text>
                            )}
                        </div>

                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>模块下的接口（{members.length}）</div>
                            <Table
                                size="small"
                                rowKey="apiCode"
                                pagination={false}
                                dataSource={members}
                                locale={{ emptyText: <Empty description="还没有接口，点右上角添加" /> }}
                                columns={[
                                    {
                                        title: '接口',
                                        key: 'api',
                                        render: (_, row) => (
                                            <div>
                                                <div style={{ fontWeight: 600 }}>{row.apiName || row.apiCode}</div>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {row.method ? `${row.method} ` : ''}{row.path || row.apiCode}
                                                </Text>
                                            </div>
                                        ),
                                    },
                                    { title: '来源系统', dataIndex: 'sourceSystem', width: 150 },
                                    {
                                        title: '归属',
                                        key: 'primary',
                                        width: 110,
                                        render: (_, row) => (row.primary
                                            ? <Tag color="blue">主模块</Tag>
                                            : <Tag>引用</Tag>),
                                    },
                                    {
                                        title: '访问范围',
                                        key: 'scope',
                                        width: 100,
                                        render: (_, row) => (row.isPublic
                                            ? <Tag color="green">公开</Tag>
                                            : <Tag>不公开</Tag>),
                                    },
                                    {
                                        title: '工具',
                                        dataIndex: 'toolCount',
                                        width: 70,
                                        render: (value) => value || 0,
                                    },
                                    {
                                        title: '操作',
                                        key: 'action',
                                        width: 150,
                                        render: (_, row) => (
                                            <Space size={4}>
                                                {row.primary ? null : (
                                                    <Button size="small" type="link" onClick={() => this.setPrimary(row)}>设为主模块</Button>
                                                )}
                                                <Button size="small" type="link" danger onClick={() => this.removeMember(row)}>移除</Button>
                                            </Space>
                                        ),
                                    },
                                ]}
                            />
                        </div>
                    </Space>
                )}
            </Drawer>
        )
    }

    renderFormModal = () => {
        const { formVisible, formSubmitting, editingModule } = this.state
        return (
            <Modal
                title={editingModule ? `编辑模块 · ${editingModule.moduleName}` : '新建能力模块'}
                visible={formVisible}
                confirmLoading={formSubmitting}
                onOk={this.submitForm}
                onCancel={() => this.setState({ formVisible: false, editingModule: null })}
                okText="保存"
                cancelText="取消"
                width={620}
                destroyOnClose
            >
                <Form layout="vertical" ref={this.formRef} initialValues={{ sensitiveLevel: 'low', status: 'draft' }}>
                    <Form.Item
                        name="moduleCode"
                        label="模块编码"
                        rules={[
                            { required: true, message: '请输入模块编码' },
                            { pattern: /^[a-z0-9][a-z0-9._-]{1,63}$/, message: '只能是小写字母、数字、点、下划线或短横线' },
                        ]}
                    >
                        <Input placeholder="一级域.能力，例如 purchase.arrival" disabled={!!editingModule} />
                    </Form.Item>
                    <Form.Item name="moduleName" label="模块名称" rules={[{ required: true, message: '请输入模块名称' }]}>
                        <Input placeholder="用业务语言，例如 采购到货查询" />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="domainCode" label="业务域编码" rules={[{ required: true, message: '请输入业务域编码' }]}>
                                <Input placeholder="例如 purchase" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="domainName" label="业务域名称" rules={[{ required: true, message: '请输入业务域名称' }]}>
                                <Input placeholder="例如 采购与到货" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="permissionSource"
                        label="权限来源"
                        extra="写清楚数据范围由哪个系统的权限决定，例如 OMS 采购单权限 + WMS 仓库权限；不填则模块只能是草稿"
                    >
                        <Input placeholder="OMS 采购单权限 + WMS 仓库权限" />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={8}>
                            <Form.Item name="owner" label="负责人">
                                <Input placeholder="姓名" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="sensitiveLevel" label="敏感级别">
                                <Select options={[
                                    { label: '低', value: 'low' },
                                    { label: '中', value: 'medium' },
                                    { label: '高', value: 'high' },
                                ]} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="status" label="状态">
                                <Select options={Object.keys(STATUS_META).map((key) => ({
                                    label: STATUS_META[key].label,
                                    value: key,
                                }))} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="sampleQuestions" label="示例问句" extra="每行一条，最多 5 条，会写入使用者提示词">
                        <TextArea rows={3} placeholder={'这周还有哪些采购单没到货？\n上海仓未来 7 天的到货计划'} />
                    </Form.Item>
                    <Form.Item name="remark" label="说明">
                        <TextArea rows={2} placeholder="这个能力能回答什么问题" />
                    </Form.Item>
                </Form>
            </Modal>
        )
    }

    renderMemberModal = () => {
        const {
            memberVisible, memberLoading, memberSubmitting, memberSelectedCodes, memberPrimary,
            candidates, candidateLoading, candidateTotal, candidatePage, candidateKeyword, candidateSource,
        } = this.state
        const module = this.state.detail?.module
        return (
            <Modal
                title={`添加接口到「${module?.moduleName || ''}」`}
                visible={memberVisible}
                width={900}
                confirmLoading={memberSubmitting}
                onOk={this.submitMembers}
                onCancel={() => this.setState({ memberVisible: false })}
                okText={`加入模块（已选 ${memberSelectedCodes.length}）`}
                cancelText="取消"
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12, borderRadius: 10 }}
                    message="下面列出的是尚未归类的接口。模块只承载查询能力，标记为「写」的接口不可加入；已在其他模块的接口也能加入，会以引用方式并存，不会从原模块移除。"
                />
                <Space style={{ marginBottom: 12 }} wrap>
                    <Input.Search
                        allowClear
                        placeholder="搜索接口名称、编码、路径"
                        style={{ width: 280 }}
                        onSearch={(value) => this.setState({ candidateKeyword: value, candidatePage: 1 }, this.loadCandidates)}
                    />
                    <Input
                        allowClear
                        placeholder="来源系统"
                        style={{ width: 180 }}
                        value={candidateSource}
                        onChange={(e) => this.setState({ candidateSource: e.target.value })}
                        onPressEnter={() => this.setState({ candidatePage: 1 }, this.loadCandidates)}
                    />
                    <Radio.Group
                        value={memberPrimary}
                        onChange={(e) => this.setState({ memberPrimary: e.target.value })}
                    >
                        <Radio.Button value>主模块</Radio.Button>
                        <Radio.Button value={false}>引用</Radio.Button>
                    </Radio.Group>
                </Space>
                <Table
                    size="small"
                    rowKey="apiCode"
                    loading={candidateLoading || memberLoading}
                    dataSource={candidates}
                    rowSelection={{
                        selectedRowKeys: memberSelectedCodes,
                        onChange: (keys) => this.setState({ memberSelectedCodes: keys }),
                        // 模块只承载查询能力，写操作接口不可勾选
                        getCheckboxProps: (row) => ({ disabled: row.query === false }),
                    }}
                    pagination={{
                        current: candidatePage,
                        pageSize: 20,
                        total: candidateTotal,
                        showSizeChanger: false,
                        onChange: (page) => this.setState({ candidatePage: page }, this.loadCandidates),
                    }}
                    columns={[
                        {
                            title: '接口',
                            key: 'api',
                            render: (_, row) => (
                                <div>
                                    <div style={{ fontWeight: 600 }}>{row.apiName || row.apiCode}</div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {row.method ? `${row.method} ` : ''}{row.path || row.apiCode}
                                    </Text>
                                </div>
                            ),
                        },
                        { title: '来源系统', dataIndex: 'sourceSystem', width: 160 },
                        {
                            title: '类型',
                            key: 'kind',
                            width: 90,
                            render: (_, row) => (row.query === false ? <Tag color="red">写</Tag> : <Tag color="blue">查询</Tag>),
                        },
                        {
                            title: '访问范围',
                            key: 'scope',
                            width: 110,
                            render: (_, row) => (row.isPublic ? <Tag color="green">公开</Tag> : <Tag>不公开</Tag>),
                        },
                    ]}
                />
                <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
                    新增接口默认不公开，需要在接口管理页单独设为公开后，应用侧才能授权给使用者。
                </Paragraph>
                <div style={{ marginTop: 16, padding: 12, background: '#f8fafc', borderRadius: 10, border: '1px solid #eef2f7' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>按来源系统整批加入</div>
                    <Space wrap>
                        <Input
                            style={{ width: 240 }}
                            placeholder="来源系统，例如 lechun_oms_v2"
                            value={this.state.batchSourceSystem}
                            onChange={(event) => this.setState({ batchSourceSystem: event.target.value })}
                        />
                        <Button loading={memberSubmitting} onClick={this.submitMembersBySource}>整批加入</Button>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            只处理该来源系统下还没归类的接口，单次上限 200 条
                        </Text>
                    </Space>
                </div>
            </Modal>
        )
    }

    renderSuggestModal = () => {
        const { suggestVisible, suggestLoading, suggestions } = this.state
        const highCount = suggestions.filter((row) => row.confidence === 'high').length
        return (
            <Modal
                title="批量归类建议"
                visible={suggestVisible}
                width={900}
                footer={[
                    <Button key="close" onClick={() => this.setState({ suggestVisible: false })}>关闭</Button>,
                    <Button key="adopt" type="primary" ghost onClick={this.adoptAllHighConfidence}>
                        采纳全部高置信度（{highCount}）
                    </Button>,
                ]}
                onCancel={() => this.setState({ suggestVisible: false })}
            >
                <Table
                    size="small"
                    rowKey="apiCode"
                    loading={suggestLoading}
                    dataSource={suggestions}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    locale={{ emptyText: <Empty description="没有未归类接口" /> }}
                    columns={[
                        {
                            title: '接口',
                            key: 'api',
                            render: (_, row) => (
                                <div>
                                    <div style={{ fontWeight: 600 }}>{row.apiName || row.apiCode}</div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {row.method ? `${row.method} ` : ''}{row.path || row.apiCode}
                                    </Text>
                                </div>
                            ),
                        },
                        { title: '来源系统', dataIndex: 'sourceSystem', width: 150 },
                        {
                            title: '建议模块',
                            key: 'suggest',
                            width: 200,
                            render: (_, row) => row.suggestedModuleName
                                ? <span>{row.suggestedModuleName}</span>
                                : <Text type="secondary">不建议开放</Text>,
                        },
                        {
                            title: '置信度',
                            dataIndex: 'confidence',
                            width: 90,
                            render: (value) => {
                                const meta = CONFIDENCE_META[value] || CONFIDENCE_META.none
                                return <Tag color={meta.color}>{meta.label}</Tag>
                            },
                        },
                        {
                            title: '理由',
                            dataIndex: 'reason',
                            render: (value) => <Text type="secondary" style={{ fontSize: 12 }}>{value || '-'}</Text>,
                        },
                        {
                            title: '操作',
                            key: 'action',
                            width: 100,
                            render: (_, row) => (
                                <Button
                                    size="small"
                                    type="link"
                                    disabled={!row.suggestedModuleCode}
                                    onClick={() => this.adoptSuggestion(row)}
                                >
                                    采纳
                                </Button>
                            ),
                        },
                    ]}
                />
            </Modal>
        )
    }

    render() {
        const { loading, modules, keyword, domainCode, status } = this.state
        const filtered = modules.filter((item) => {
            if (keyword) {
                const lower = keyword.toLowerCase()
                const hit = [item.moduleCode, item.moduleName, item.owner]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(lower))
                if (!hit) return false
            }
            if (domainCode && item.domainCode !== domainCode) return false
            if (status && item.status !== status) return false
            return true
        })
        return (
            <div style={{ padding: 4 }}>
                <Alert
                    type="warning"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    style={{ marginBottom: 16, borderRadius: 12 }}
                    message="接口归属按 api_code 关联，接口重扫不会影响已建立的归属关系"
                />
                {this.renderHero()}
                {this.renderProgressBar()}
                {this.renderToolbar()}
                <Card bordered={false} style={{ borderRadius: 20, border: '1px solid #edf1f7' }}>
                    <Table
                        rowKey="moduleCode"
                        loading={loading}
                        dataSource={filtered}
                        columns={this.renderColumns()}
                        scroll={{ x: 1280 }}
                        pagination={{ pageSize: 20, showSizeChanger: false }}
                        locale={{ emptyText: <Empty description="还没有能力模块，先新建一个" /> }}
                    />
                </Card>
                {this.renderDetailDrawer()}
                {this.renderFormModal()}
                {this.renderMemberModal()}
                {this.renderSuggestModal()}
            </div>
        )
    }
}

